/** Never send privileged platform APIs arbitrary schemes, credentials or control characters. */
export function externalUrl(value: string): URL {
  if (/[\u0000-\u0020\u007f]/.test(value)) throw new Error('Invalid external URL')
  const url = new URL(value)
  if (!['https:', 'http:'].includes(url.protocol) || url.username || url.password) throw new Error('Expected a public HTTP(S) URL')
  return url
}
/** Build a compose-only mail link without allowing extra recipients or headers. */
export function emailUrl(to: string, subject: string): string {
  if (!/^[A-Za-z0-9.!#$&'*+\/=^_`{|}~-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/.test(to)) throw new Error('Expected one email recipient')
  if (/[\u0000-\u001f\u007f]/.test(subject)) throw new Error('Invalid email subject')
  return `mailto:${encodeURIComponent(to).replace('%40', '@')}?subject=${encodeURIComponent(subject)}`
}
export function internalPath(value: string): string {
  if (!value.startsWith('/') || value.startsWith('//') || /[\\\u0000-\u0020\u007f]/.test(value)) throw new Error('Expected an application path')
  const url = new URL(value, 'https://app.invalid')
  if (url.origin !== 'https://app.invalid') throw new Error('Application path changes origin')
  let decoded = url.pathname
  for (let count = 0; count < 8; count++) {
    const next = decodeURIComponent(decoded)
    if (next.startsWith('//') || /[\\\u0000-\u0020\u007f]/.test(next)) throw new Error('Unsafe encoded application path')
    if (next === decoded) return url.pathname + url.search + url.hash
    decoded = next
  }
  throw new Error('Application path is encoded too deeply')
}
export function publicUrl(base: string, value: string): string {
  const origin = externalUrl(base)
  if (origin.pathname !== '/' || origin.search || origin.hash) throw new Error('Expected a public application origin')
  return new URL(internalPath(value), origin).href
}
export function safeFilename(value: string): string {
  const name = value.replace(/[\\/\u0000-\u001f\u007f<>:"|?*]/g, '_').trim().replace(/[. ]+$/g, '')
  if (!name || name === '.' || name === '..') return 'download'
  const reserved = /^(con|prn|aux|nul|com[0-9]|lpt[0-9])(?:\.|$)/i
  const portable = reserved.test(name) ? `_${name}` : name
  const encoder = new TextEncoder()
  if (encoder.encode(portable).length <= 180) return portable
  const extension = portable.match(/\.[a-z0-9]{1,12}$/i)?.[0] || ''
  const stem = extension ? portable.slice(0, -extension.length) : portable
  const budget = 180 - encoder.encode(extension).length
  let result = '', bytes = 0
  for (const character of stem) {
    const size = encoder.encode(character).length
    if (bytes + size > budget) break
    result += character; bytes += size
  }
  const filename = (result + extension).replace(/[. ]+$/g, '') || 'download'
  return reserved.test(filename) ? `_${filename}` : filename
}
